import React, { useState } from 'react';
import { files as filesApi } from '../services/api';
import { Building2 } from 'lucide-react';

/**
 * Small circular business-logo avatar overlaid at the bottom-left of a
 * card's hero image. Uses `object-fit: contain` on a **white background**
 * so the full logo is visible without cropping — critical for
 * text-based / non-square logos.
 *
 * Falls back to a Lucide Building icon when neither `logo` nor legacy
 * `image` is set. Never rendered on top of an uploaded image as a
 * "replacement" — this is only the small badge overlay.
 */
export default function BusinessLogoAvatar({ business, size = 'md', className = '' }) {
  const [errored, setErrored] = useState(false);
  const src = business?.logo || business?.image;
  const has = !!src && !errored;

  const dim =
    size === 'sm' ? 'w-10 h-10' :
    size === 'lg' ? 'w-[72px] h-[72px]' :
    'w-14 h-14';
  const iconSize = size === 'sm' ? 18 : size === 'lg' ? 30 : 22;

  return (
    <div
      data-testid="business-logo-avatar"
      className={`${dim} rounded-full overflow-hidden ring-4 ring-black/40 shadow-lg bg-white flex items-center justify-center ${className}`}
    >
      {has ? (
        <img
          src={filesApi.urlFor(src)}
          alt={business?.name ? `${business.name} logo` : 'Business logo'}
          className="w-full h-full object-contain object-center p-1"
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
        />
      ) : (
        <Building2 size={iconSize} className="text-emerald-700" />
      )}
    </div>
  );
}
